<script setup lang="ts">

import { ref } from "vue";
import dayjs from "dayjs";
import { comma } from "@library/Utilities";
import { useCarePlanApi } from "@app/MyGarage/Types/Api/CarePlanApi";
import { useViewModelInstance } from "@app/MyGarage/Types/MyGarageViewModelStore";
import { formatPhone } from "@modules/Utilities/Formatters";
import { FeatureFrame } from "../Components";
import type { ICarbucksProtectFeature } from "@domain/CarePlan";

import CarbucksProtectFeatureIcons from "../Components/CarbucksProtectFeatureIcons.vue";
import { FeatureStatus } from "@domain/General/Enums/FeatureStatus";
import ProtectStatutoryWarning from "@app/MyGarage/Components/ProtectStatutoryWarning/ProtectStatutoryWarning.vue";

const model = defineModel<ICarbucksProtectFeature>( { required: true } ); 
const viewModel = await useViewModelInstance();
const attributes = [
    { title: 'Term of cover', value: model.value.termInMonths + " months" },
    { title: 'Kilometer limit', value: model.value.kilometerLimit < 1 ? 'NO LIMIT' : `${comma( model.value.kilometerLimit )} km` },
    { title: 'Phone number', value: formatPhone( model.value.phoneNumber ) },
].filter( a => a.value != undefined && a.value.length > 0 );

const api = useCarePlanApi();
const documents = await api.getCarbucksProtectDocumentUrls( viewModel.vehicle.vehicleId );
const termsAndConditionsLinkUrl = ref( documents?.termsAndConditionsLinkUrl );
const documentsLinkUrl = ref( documents?.featureSummaryLinkUrl );

if( model.value.isPending == false )
{
    attributes.push(
        { title: 'Active from', value: dayjs( model.value.dateActiveFrom ).format( 'Do MMM YYYY' ) },
        { title: 'Expires', value: dayjs( model.value.dateActiveTo ).format( 'Do MMM YYYY' ) },
    )
} 

</script>

<template>

    <FeatureFrame :featured="true">
        
        <template #title>
            <div class="w-full px-8 2xl:mx-10 my-2">
                <img src="@/assets/images/protect-logo.png" class="w-full"/>
            </div>
        </template>

        <template #sub-title>
            <div class="px-2 3xl:px-4 text-base text-center -mb-2">
                Protects you from wear and tear,
                saving you time and money when you service your car
            </div>
        </template>

        <template #features="{ close }">
            <div class="flex flex-wrap justify-center tracking-wider
                        text-sm 3xl:text-xl pb-2">
                
                <h1 class="basis-full text-center uppercase my-3 opacity-80 text-white/60 text-xs 3xl:text-lg">
                    Included with PROTECT
                </h1>

                <CarbucksProtectFeatureIcons :model="model" />

            </div>
        </template>

        <div class="flex flex-col w-full">

            <!-- PROPERTIES -->
            <div class="flex flex-col w-full gap-1 pt-4 3xl:text-lg
                        text-xs lg:text-sm tracking-wider">
               
                <div v-if="model.isActive">
                    <div class="text-xs 3xl:!text-base 3xl:py-2 text-emerald-500 uppercase mb-2 text-center">
                        Your PROTECT cover is active
                    </div>
                </div>
                
                <div v-if="model.provider" class="flex items-start gap-2 3xl:py-1">
                    <div>Provider</div>
                    <div class="flex-1 border-b-2 border-dotted opacity-60 h-[1rem]" />
                    <div class="uppercase text-right">{{ model.provider }}</div>
                </div>  

                <div v-for="attribute in attributes" class="flex items-start gap-2 3xl:py-1">
                    <div>{{ attribute.title }}</div>
                    <div class="flex-1 border-b-2 border-dotted opacity-60 h-[1rem]" />
                    <div class="text-right">{{ attribute.value }}</div>
                </div>  
                
                <div v-if="model.isPending"
                     class="grid grid-cols-3 mt-2">
                    <div class="col-span-3 opacity-60">
                        Your PROTECT cover needs to be acknowledged before it becomes active.  You should activate it now
                    </div>
                </div> 
            </div>           
            
            <ProtectStatutoryWarning class="col-span-3 text-center mt-4" :is-used="model.isUsed" :contract-start-date="model.dateActiveFrom" />
            
            <!-- ACTIONS -->
            <div class="w-full flex flex-col
                        gap-x-1 gap-y-3 md:gap-x-2 md:gap-y-4 3xl:gap-4 mt-4 3xl:mt-6">

                <div v-if="model.isActive" 
                     class="my-3">
                    <div class="text-xs 3xl:text-base opacity-50 text-center">
                        Quote contract number <strong>{{ model.subscriberNumber }}</strong>
                    </div>
                    <JupiterButton 
                        variant="tonal"
                        type="tel" 
                        :to="model.phoneNumber"
                        class="flex !h-12 lg:!h-14 3xl:!h-16 w-full my-1 lg:my-2 3xl:my-3">
                        <span ref="button"
                            class="flex items-center gap-1 3xl:gap-3">
                            <Icon name="help-me:insurance" class="text-3xl -mt-1 mr-1 opacity-80" />
                            <span class="3xl:text-base">
                                Call To Make a Claim
                            </span>
                        </span>
                    </JupiterButton>    
                    <div class="opacity-60 text-xs 3xl:text-base text-center">
                        Refer to docs for full Terms &amp; Conditions
                    </div>
                </div>

                <JupiterButton 
                    v-if="termsAndConditionsLinkUrl"  
                    variant="outlined"
                    color="white/50"
                    type="url" 
                    :to="termsAndConditionsLinkUrl"
                    class="col-span-2 !h-10 lg:!h-12 3xl:!h-14">
                    <span class="flex items-center gap-1 text-white">
                        <Icon name="contract" class="text-2xl !hidden md:!block" />
                        <span class="3xl:text-base">
                            Terms &amp; Conditions
                        </span>
                    </span>
                </JupiterButton>

                <JupiterButton 
                    v-if="documentsLinkUrl"
                    variant="outlined"
                    type="url" 
                    color="white/50"
                    class="col-span-2 !h-10 lg:!h-12 3xl:!h-14"
                    :to="documentsLinkUrl">
                    <span class="flex items-center gap-1 text-white">
                        <Icon name="contract" class="text-2xl !hidden md:!block" />
                        <span class="3xl:text-base">
                            Documents
                        </span>
                    </span>
                </JupiterButton>
                
                <Button 
                    v-if="model.status == FeatureStatus.Pending"
                    variant="tonal"
                    type="tel" 
                    to="/my-garage/acknowledge-carbucks-protect"
                    class="flex !h-10 lg:!h-12 3xl:!h-14 w-full">
                    <span ref="button"
                        class="flex items-center gap-1 3xl:gap-3">
                        <Icon name="help-me:insurance" class="text-3xl -mt-1 mr-1 opacity-80" />
                        <span class="3xl:text-base">
                            Activate PROTECT
                        </span>
                    </span>
                </Button>    

                <div class="-mb-2 -mt-2 flex justify-stretch w-full">
                    <Button variant="text"
                            to="/my-garage/concierge"
                            class="!h-10 lg:!h-12 3xl:!h-14 grow">
                        More Options
                    </Button>
                    
                    <Button variant="text"
                            to="/my-garage/dealership"
                            class="!h-10 lg:!h-12 3xl:!h-14 grow">
                        Contact Dealer
                    </Button>
                </div>
                
            </div>
        </div>
   </FeatureFrame>
        
</template>
